import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { PhX } from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';

// ---------------------------------------------------------------------------
// Sub-components: Header, Body, Footer
// ---------------------------------------------------------------------------

interface ModalHeaderSignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const ModalHeader: TOC<ModalHeaderSignature> = <template>
  <div data-test-modal-header class="flex-none px-6 py-4 border-b border-gray-200" ...attributes>
    {{yield}}
  </div>
</template>;

interface ModalBodySignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const ModalBody: TOC<ModalBodySignature> = <template>
  <div data-test-modal-body class="flex-1 overflow-y-auto px-6 py-4" ...attributes>
    {{yield}}
  </div>
</template>;

interface ModalFooterSignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const ModalFooter: TOC<ModalFooterSignature> = <template>
  <div data-test-modal-footer class="flex-none px-6 py-4 border-t border-gray-200" ...attributes>
    {{yield}}
  </div>
</template>;

// ---------------------------------------------------------------------------
// Size config
// ---------------------------------------------------------------------------

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'w-full max-w-none',
};

// ---------------------------------------------------------------------------
// Main Modal component
// ---------------------------------------------------------------------------

/**
 * Reimagined Modal — zero-Frontile dialog overlay with premium polish.
 *
 * Features:
 * - Frosted glass backdrop (backdrop-blur-sm)
 * - Spring-feel scale+opacity entrance animation
 * - Escape key & backdrop click dismiss
 * - Moves focus into the dialog on open
 * - Body scroll lock while open
 * - Named blocks for header, body, footer
 *
 * @example Basic centered modal
 * ```gts
 * import Modal from 'tio-reimagined/components/modal';
 *
 * <Modal @isOpen={{this.showModal}} @onClose={{this.closeModal}} @size="md">
 *   <:header>
 *     <h2 class="text-lg font-semibold">Edit Profile</h2>
 *   </:header>
 *   <:body>
 *     <p>Modal content here.</p>
 *   </:body>
 *   <:footer>
 *     <div class="flex justify-end gap-3">
 *       <Button @appearance="outlined" @intent="default">Cancel</Button>
 *       <Button @intent="primary">Save</Button>
 *     </div>
 *   </:footer>
 * </Modal>
 * ```
 */
interface ModalSignature {
  Args: {
    /** Whether the modal is open */
    isOpen: boolean;
    /** Called when the modal requests to close */
    onClose: () => void;
    /** Max-width of the modal panel @default "md" */
    size?: ModalSize;
    /** Close when clicking the backdrop @default true */
    closeOnOutsideClick?: boolean;
    /** Close when pressing Escape @default true */
    closeOnEscapeKey?: boolean;
    /** Show the default close button in the top-right corner @default true */
    allowCloseButton?: boolean;
    /** Accessible label for the dialog (used as aria-label) */
    ariaLabel?: string;
  };
  Blocks: {
    /** Default block — free-form content */
    default?: [];
    /** Named block for header content */
    header?: [];
    /** Named block for body content */
    body?: [];
    /** Named block for footer content */
    footer?: [];
  };
  Element: HTMLDivElement;
}

export default class Modal extends Component<ModalSignature> {
  @tracked private _mounted = false;
  @tracked private _visible = false;

  /** Unique ID for this modal instance — used to scope querySelector */
  private modalId = crypto.randomUUID();

  /** Portal target — always renders to document.body (use document.querySelector in tests) */
  get portalDestination(): Element {
    return document.body;
  }

  private get size() {
    return this.args.size ?? 'md';
  }

  private get closeOnOutsideClick() {
    return this.args.closeOnOutsideClick ?? true;
  }

  private get closeOnEscapeKey() {
    return this.args.closeOnEscapeKey ?? true;
  }

  private get showCloseButton() {
    return this.args.allowCloseButton ?? true;
  }

  private get panelSizeClass() {
    return SIZE_CLASSES[this.size];
  }

  private get isFullSize() {
    return this.size === 'full';
  }

  // --------------------------------------------------
  // Lifecycle modifier — mounts/unmounts with animation
  // --------------------------------------------------

  manageFocus = modifier((_element: HTMLElement, [isOpen]: [boolean]) => {
    let rafId: number | undefined;
    let savedScrollY = 0;

    if (isOpen) {
      this._mounted = true;

      // Lock body scroll
      savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      // Trigger the scale-in on the next frame
      rafId = requestAnimationFrame(() => {
        rafId = undefined;
        this._visible = true;

        const dialog = document.querySelector<HTMLElement>(`[data-modal-id="${this.modalId}"]`);
        if (dialog) {
          dialog.focus({ preventScroll: true });
        }
      });
    } else if (this._mounted) {
      this._visible = false;

      const cleanup = setTimeout(() => {
        this._mounted = false;
      }, 300);

      return () => clearTimeout(cleanup);
    }

    return () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      // Restore body scroll
      if (document.body.style.position === 'fixed') {
        const scrollY = savedScrollY;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      }
    };
  });

  // --------------------------------------------------
  // Event handlers
  // --------------------------------------------------

  handleBackdropClick = () => {
    if (this.closeOnOutsideClick) {
      this.args.onClose();
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (this.closeOnEscapeKey && event.key === 'Escape') {
      event.stopPropagation();
      this.args.onClose();
    }
  };

  <template>
    {{! Anchor element for the lifecycle modifier }}
    <div
      class="reimagined-modal-anchor sr-only"
      {{this.manageFocus @isOpen}}
      aria-hidden="true"
    ></div>

    {{#if this._mounted}}
      {{#in-element this.portalDestination insertBefore=null}}
        <div
          data-test-modal
          data-modal-id={{this.modalId}}
          class="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={{@ariaLabel}}
          tabindex="-1"
          {{on "keydown" this.handleKeyDown}}
          ...attributes
        >
          {{! Frosted glass backdrop }}
          {{#if this.closeOnOutsideClick}}
            {{! template-lint-disable no-nested-interactive }}
            <button
              data-test-modal-backdrop
              type="button"
              class="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 cursor-default
                {{if this._visible 'opacity-100' 'opacity-0'}}"
              aria-label="Close"
              tabindex="-1"
              {{on "click" this.handleBackdropClick}}
            ></button>
          {{else}}
            <div
              data-test-modal-backdrop
              class="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300
                {{if this._visible 'opacity-100' 'opacity-0'}}"
              aria-hidden="true"
            ></div>
          {{/if}}

          {{! Modal panel with spring-feel entrance }}
          <div
            data-test-modal-panel
            class="relative w-full
              {{this.panelSizeClass}}
              {{if this.isFullSize 'h-full' 'max-h-[85vh]'}}
              flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-2xl transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]
              {{if
                this._visible
                'opacity-100 scale-100 translate-y-0'
                'opacity-0 scale-95 translate-y-4'
              }}"
          >
            {{! Close button }}
            {{#if this.showCloseButton}}
              <button
                data-test-modal-close
                type="button"
                class="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                {{! template-lint-disable no-bare-strings }}
                aria-label="Close modal"
                {{on "click" @onClose}}
              >
                <PhX @size={{20}} />
              </button>
            {{/if}}

            {{! Named block API }}
            {{#if (has-block "header")}}
              <ModalHeader>{{yield to="header"}}</ModalHeader>
            {{/if}}

            {{#if (has-block "body")}}
              <ModalBody>{{yield to="body"}}</ModalBody>
            {{/if}}

            {{#if (has-block "footer")}}
              <ModalFooter>{{yield to="footer"}}</ModalFooter>
            {{/if}}

            {{! Default block }}
            {{#if (has-block)}}
              {{yield}}
            {{/if}}
          </div>
        </div>
      {{/in-element}}
    {{/if}}
  </template>
}

export type { ModalSignature, ModalSize };
